
import Page from '@/Page.vue';
import { Component, Prop, PropSync, Ref } from 'vue-property-decorator';
import { CrudAction, CrudReponse } from '@/libs/core/+state/models/crud-action';
import {
    CategoryModel,
    EscalationGroupTypeEnum,
    FindingFileModel,
    FindingModel,
    ImplementationStateEnum,
    MeasureModel,
    MeasureVerificationUserModel,
    MeasureVerificationUserTypeEnum,
    PermissionTypeEnum,
    PlanItemModel,
    ProcessTypeEnum,
    QueryOperator,
    ResponseBasicModel,
    ResponseModel,
    TenantUserModel,
} from '@/libs/Api';
import WorkTaskFindingItemMeasure from '../components/WorkTaskFindingItemMeasure.vue';
import { cloneDeep } from 'lodash';
import { measuresStore } from '@/libs/measures/+state/store';
import moment from 'moment';
import { planItemsStore } from '@/libs/plan-items/+state/store';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import { tenantUsersStore } from '@/libs/tenants/+state/store';
import { Guid } from '@/libs/common/functions/guid';
import { authStore } from '@/libs/auth/+store/store';
import { AuthGetter } from '@/libs/auth/models/auth-state';
import WorkTaskDialogFiles from '../components/WorkTaskDialogFiles.vue';
import WorkTaskEscalation from '@/libs/work-tasks/components/WorkTaskEscalation.vue';
import WorkTaskEndShutdown from '@/libs/work-tasks/components/WorkTaskEndShutdown.vue';
import { createCrudQueryPayload } from '@/libs/core/+state/models/crud-query-payload';
import { formatDate } from '@/libs/common/functions/format-date';
import { findingsStore } from '@/libs/findings/+state/store';

@Component({
    components: {
        WorkTaskEndShutdown,
        WorkTaskEscalation,
        WorkTaskFindingItemMeasure,
        WorkTaskDialogFiles,
    },
})
export default class WorkTaskMeasureItem extends Page {
    /* Component props */
    @PropSync('measure') item!: MeasureModel;
    @Prop() planItem!: PlanItemModel;
    @Prop() category!: CategoryModel;
    @Prop() finding!: FindingModel;
    @Prop({ default: false }) readonly!: boolean;
    @Prop({ default: false }) standalone!: boolean;
    @Prop({ default: false }) showStation!: boolean;
    @Prop({ default: false }) dense!: boolean;

    /* Component state variables */
    dialog = false;
    valid = true;
    saving = false;
    hasChanges = true;
    dialogValid = true;
    invalidDialogVerificationUsers = false;
    endShutdownDialog = false;
    isEndShutdownMandatory = false;
    dialogModel: MeasureModel | null = null;
    dialogVerificationUsers: string[] = [];
    dialogFindingCause: string | null = null;

    /* Auth variables */
    tenantUser: TenantUserModel = authStore.useGetter(AuthGetter.GetTenantUser) as TenantUserModel;

    protected EscalationGroupTypeEnum: typeof EscalationGroupTypeEnum = EscalationGroupTypeEnum;
    protected ProcessTypeEnum: typeof ProcessTypeEnum = ProcessTypeEnum;
    protected ImplementationStateEnum: typeof ImplementationStateEnum = ImplementationStateEnum;

    /* Ref */
    @Ref() dialogForm!: any;

    get isDialogReadonly(): boolean {
        return this.item?.implementationState == ImplementationStateEnum.Verified || this.readonly;
    }

    get storedPlanItem(): PlanItemModel {
        return planItemsStore.useGetter(CrudGetter.Detail, this.item.finding?.response?.planItemId) as PlanItemModel;
    }

    get storedResponse(): ResponseModel | null {
        const planItem = planItemsStore.useGetter(CrudGetter.Detail, this.item.finding?.response?.planItemId) as PlanItemModel;
        return planItem?.responses?.firstOrDefault((x) => x.id == this.finding.responseId) ?? null;
    }

    get canSelectVerificationUser(): boolean {
        if (this.category.processType != ProcessTypeEnum.Audit) {
            return true;
        }

        if (this.planItem.audit!.category!.measureVerificationUserType == MeasureVerificationUserTypeEnum.UserSelect) {
            return true;
        }

        const auditPerformerUserId = this.planItem.tenantUserId!;
        const tenantUser = (tenantUsersStore.useGetter(CrudGetter.Data) as TenantUserModel[]).firstOrDefault(
            (x) => x.id == auditPerformerUserId
        );

        return tenantUser == null;
    }

    // ✅ NOVÝ SYSTÉM: Kontroluje readonly podle kategorie-specifických oprávnění
    get isReadonly(): boolean {
        // Kontrola oprávnění v kontextu kategorie
        const hasPersmissionToEdit = this.hasAccessToCategory(
            this.category.id,
            [PermissionTypeEnum.Measures, PermissionTypeEnum.MeasuresAllUsers]
        );
        
        // Musí být také implementerem opatření
        const isMeasureImplementer = this.item.measureImplementationUsers!.some((n) => n.tenantUserId! == this.tenantUser.id!);
        
        return !hasPersmissionToEdit || !isMeasureImplementer;
    }

    get response(): ResponseBasicModel {
        return this.finding.response!;
    }

    get findingFiles(): FindingFileModel[] {
        return this.finding?.findingFiles ?? [];
    }

    mounted() {
        if (this.standalone && this.item.finding?.response?.planItemId) {
            planItemsStore.dispatch(CrudAction.Get, {
                id: this.item.finding.response.planItemId,
                key: this.item.finding.response.planItemId,
            });
        }
    }

    /* Action functions */

    openDialog() {
        this.dialogModel = cloneDeep(this.item);
        this.dialogFindingCause = this.finding?.cause ?? null;
        if (
            (this.category.processType == ProcessTypeEnum.Maintenance ||
                this.category.processType == ProcessTypeEnum.Checklist ||
                this.category.processType == ProcessTypeEnum.ProductInspection ||
                this.category.processType == ProcessTypeEnum.IssueSolving) &&
            this.dialogModel.implementerUserId == null
        ) {
            this.dialogModel.implementerUserId = this.tenantUser.id!;
        }
        this.dialogVerificationUsers =
            this.dialogModel.measureVerificationUsers
                ?.select((x) => x.tenantUserId ?? Guid.EmptyGuid())
                .distinct()
                .toArray() ?? [];
        this.dialogValid = true;
        this.invalidDialogVerificationUsers = false;
        this.dialog = true;
    }

    closeDialog() {
        this.dialog = false;
    }

    async save() {
        const invalidDialogVerificationUsers = this.validateDialogVerificationUsers();
        if (this.dialogForm.validate() && !invalidDialogVerificationUsers) {
            this.saving = true;
            const dialogModel = cloneDeep(this.dialogModel);
            if (this.dialogFindingCause !== this.finding?.cause) {
                // Nejprve načti aktuální stav položky ze serveru
                findingsStore.dispatch(CrudAction.Get, { id: this.finding.id });
                const finding = await this.subscribe(findingsStore, CrudReponse.Get);
                const findingToUpdate = cloneDeep(finding ?? this.finding);
                findingToUpdate.cause = this.dialogFindingCause;
                findingsStore.dispatch(CrudAction.Update, { item: findingToUpdate });
                await this.subscribe(findingsStore, CrudReponse.Update);
                this.finding.cause = this.dialogFindingCause;
            }
            if (
                dialogModel!.implementationState == ImplementationStateEnum.WaitingForVerification &&
                (this.item!.implementationState == ImplementationStateEnum.New ||
                    this.item!.implementationState == ImplementationStateEnum.InProgress)
            ) {
                if (
                    this.category.processType == ProcessTypeEnum.Maintenance ||
                    this.category.processType == ProcessTypeEnum.Checklist ||
                    this.category.processType == ProcessTypeEnum.ProductInspection ||
                    this.category.processType == ProcessTypeEnum.IssueSolving
                ) {
                    dialogModel!.implementerUserId = this.tenantUser.id;
                } else if (dialogModel!.implementerUserId == null) {
                    dialogModel!.implementerUserId = this.tenantUser.id;
                }
                dialogModel!.implementationTime = moment().format('YYYY-MM-DDTHH:mm:ss');
                dialogModel!.implementationDelayDays = moment(dialogModel!.implementationTime)
                    .endOf('day')
                    .isBefore(moment(dialogModel!.implementUntil).startOf('day'))
                    ? Math.ceil(moment(dialogModel!.implementationTime).diff(moment(dialogModel!.implementUntil), 's') / 86400)
                    : null;
                if (!this.canSelectVerificationUser && this.planItem.audit?.category?.processType == ProcessTypeEnum.Audit) {
                    const auditPerformerUserId = this.planItem.tenantUserId!;
                    const tenantUser = (tenantUsersStore.useGetter(CrudGetter.Data) as TenantUserModel[]).first(
                        (x) => x.id == auditPerformerUserId
                    );
                    dialogModel!.measureVerificationUsers = [
                        {
                            id: Guid.EmptyGuid(),
                            tenantUserId: tenantUser.id,
                        } as MeasureVerificationUserModel,
                    ];
                }
            } else if (
                dialogModel!.implementationState == ImplementationStateEnum.Verified &&
                !dialogModel?.implementerUserId
            ) {
                dialogModel!.implementationTime = moment().format('YYYY-MM-DDTHH:mm:ss');
                dialogModel!.implementerUserId = this.tenantUser.id!;
            }
            if (this.canSelectVerificationUser) {
                dialogModel!
                    .measureVerificationUsers!.where((x) => !this.dialogVerificationUsers.any((n) => n == x.tenantUserId))
                    .toArray()
                    .forEach((x) => {
                        dialogModel!.measureVerificationUsers!.splice(dialogModel!.measureVerificationUsers!.indexOf(x), 1);
                    });
                this.dialogVerificationUsers
                    .where((x) => !dialogModel!.measureVerificationUsers!.any((n) => n.tenantUserId == x))
                    .toArray()
                    .forEach((x) => {
                        dialogModel!.measureVerificationUsers!.push({
                            id: Guid.EmptyGuid(),
                            tenantUserId: x,
                        } as MeasureVerificationUserModel);
                    });
            }
            measuresStore.dispatch(CrudAction.Update, { item: dialogModel });
            this.subscribe(measuresStore, CrudReponse.Update)
                .then((e) => {
                    if (this.planItem.isWorkplaceShutdown && this.planItem.shutdownToDate == null) {
                        measuresStore.dispatch(
                            CrudAction.GetAll,
                            createCrudQueryPayload<MeasureModel>(
                                undefined,
                                [
                                    {
                                        field: 'finding.response.planItemId',
                                        op: QueryOperator.Eq,
                                        comparand: this.planItem.id!,
                                    },
                                    {
                                        field: 'implementationState',
                                        op: QueryOperator.In,
                                        comparand: [ImplementationStateEnum.New, ImplementationStateEnum.InProgress],
                                    },
                                ],
                                undefined,
                                false,
                                'NotImplementedMeasures'
                            )
                        );
                        this.subscribe(measuresStore, CrudReponse.GetAll, 'NotImplementedMeasures').then(
                            (e: MeasureModel[]) => {
                                if (e.length == 0) {
                                    this.isEndShutdownMandatory = true;
                                } else {
                                    this.isEndShutdownMandatory = false;
                                }
                                this.endShutdownDialog = true;
                                this.saving = false;
                                this.$emit('planItemChanged');
                                this.closeDialog();
                            }
                        );
                    } else {
                        this.saving = false;
                        this.$emit('planItemChanged');
                        this.closeDialog();
                    }
                })
                .catch((e) => {
                    this.saving = false;
                    console.log(e);
                });
        }
    }

    validateDialogVerificationUsers() {
        if (this.planItem.audit!.category!.measureVerificationUserType == MeasureVerificationUserTypeEnum.AuditPerformer) {
            return false;
        } else {
            this.invalidDialogVerificationUsers =
                this.dialogModel?.implementationState == ImplementationStateEnum.WaitingForVerification &&
                this.dialogVerificationUsers.length == 0;
            return this.invalidDialogVerificationUsers;
        }
    }

    protected getImplementationTenantUserList(): string[] {
        return [...(this.dialogVerificationUsers ?? [])];
    }

    protected setImplementationTenantUserList(tenantUsers: string[]) {
        if (!this.dialogVerificationUsers) {
            this.dialogVerificationUsers = [];
        }
        const existingTenantUsers = [...this.dialogVerificationUsers];
        const tenantUsersToAdd = tenantUsers.filter((x) => !existingTenantUsers.contains(x));
        const tenantUsersToDelete = existingTenantUsers.filter((x) => !tenantUsers.contains(x));
        for (const tenantUser of tenantUsersToAdd) {
            this.dialogVerificationUsers.push(tenantUser);
        }
        for (const tenantUser of tenantUsersToDelete) {
            const implementationUser = this.dialogVerificationUsers.find((x) => x == tenantUser)!;
            this.dialogVerificationUsers.splice(this.dialogVerificationUsers.indexOf(implementationUser), 1);
        }
    }

    protected getResponseValueLabel(responseValue: number): string | null {
        return this.category?.responseValues?.find((x) => x.value == responseValue)?.label ?? null;
    }
}
